import { checklistValuesToDraft } from '@/services/checklist/checklistObservations';
import { useEffect, useState, useCallback, useRef } from 'react';
import { databaseService } from '../services/databaseService';
import { APP_CONSTANTS } from '../config/constants';
import { UseChecklistEditorLogger } from '../utils/logger';

export interface Checklist {
  id: string;
  template_id: string;
  status: string;
  sequencial_numero?: number | null;
  sequencial_codigo?: string | null;
  [key: string]: any;
}

export interface ChecklistField {
  id: string;
  grupo: string;
  nome: string;
  tipo: string;
  ordem: number;
  [key: string]: any;
}

export interface ChecklistEditorRefreshOptions {
  syncRevision?: number;
  syncEnabled?: boolean;
  identityKey?: string;
  companyId?: string | null;
}

export function useChecklistEditor(id: string, options: ChecklistEditorRefreshOptions = {}) {
  const { syncRevision, syncEnabled = true, identityKey = '', companyId } = options;
  const scope = `${id}:${identityKey}:${companyId ?? ''}`;
  const refreshEnabled = useRef(syncEnabled);
  refreshEnabled.current = syncEnabled;
  const activeScope = useRef(scope);
  activeScope.current = scope;
  const mounted = useRef(false);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [template, setTemplate] = useState<ChecklistField[]>([]);
  const [templateRetired, setTemplateRetired] = useState(false);
  const [valuesRead, setValuesRead] = useState<{ values: Record<string, string> | null; version: number }>({ values: null, version: 0 });
  const readClock = useRef(0);
  const latestRead = useRef(0);
  // A ordem local distingue uma leitura antiga de outra iniciada após salvar.
  const markValuesWriteConfirmed = useCallback(() => ++readClock.current, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshChecklistOnly = useCallback(async () => {
    try {
      const c = (await databaseService.readItem(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
        id
      )) as Checklist | null;
      if (!mounted.current || activeScope.current !== scope) return;
      setChecklist(current => {
        if (!current || !c) return c;

        const currentNumero = current.sequencial_numero ?? null;
        const currentCodigo = current.sequencial_codigo?.trim() || null;
        const nextNumero = c.sequencial_numero ?? current.sequencial_numero;
        const nextCodigo = c.sequencial_codigo?.trim()
          ? c.sequencial_codigo
          : current.sequencial_codigo;
        const nextNumeroComparable = nextNumero ?? null;
        const nextCodigoComparable = nextCodigo?.trim() || null;

        if (currentNumero === nextNumeroComparable && currentCodigo === nextCodigoComparable) {
          return current;
        }

        return {
          ...current,
          sequencial_numero: nextNumero,
          sequencial_codigo: nextCodigo,
        };
      });
    } catch (err: any) {
      UseChecklistEditorLogger.Main.info(`[refreshChecklistOnly] ERRO: ${err?.message || err}`);
    }
  }, [id, scope]);

  const fetchData = useCallback(
    async (signal?: AbortSignal, silent = false) => {
      const readVersion = ++readClock.current;
      latestRead.current = readVersion;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      // NOVO LOG
      UseChecklistEditorLogger.Main.info(`[fetchData] Iniciando para checklist ${id}`);

      // Helper para verificar cancelamento antes de qualquer setState
      const isAborted = () => (silent && !refreshEnabled.current) || signal?.aborted || !mounted.current || activeScope.current !== scope || latestRead.current !== readVersion;

      try {
        const c = (await databaseService.readItem(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
          id
        )) as Checklist | null;
        // NOVO LOG
        UseChecklistEditorLogger.Main.info(`[fetchData] Checklist carregado: ${JSON.stringify(c)}`);

        if (isAborted()) return;
        if (c && companyId !== undefined && c.empresa_id !== companyId) {
          setChecklist(null);
          setValuesRead({ values: null, version: readVersion });
          setError('Checklist fora da empresa da sessão.');
          throw new Error('Checklist fora da empresa da sessão.');
        }

        let t: ChecklistField[] = [];
        let modelRetired = false;
        let vals: any[] = [];

        if (c && c.template_id) {
          t = await databaseService.listItems(
            APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
            { template_id: c.template_id, empresa_id: c.empresa_id }
          );

          const model = await databaseService.readItem<any>(
            APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME, c.template_id
          );
          modelRetired = model?.empresa_id === c.empresa_id && model?.retired_at != null;
          t = t.map(field => ({ ...field, historical_read_only: field.retired_at != null || modelRetired }));

          // Garante que ordem sempre seja numérico
          t.sort(
            (a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0)
          );
          // NOVO LOG
          UseChecklistEditorLogger.Main.info(`[fetchData] Template carregado (${t.length} campos)`);
          UseChecklistEditorLogger.Main.info(`[fetchData] Template: ${JSON.stringify(t)}`);

          if (isAborted()) return;

          vals = await databaseService.listItems(
            APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
            { checklist_id: id, empresa_id: c.empresa_id }
          );
          // NOVO LOG          
          UseChecklistEditorLogger.Main.info(`[fetchData] Valores carregados (${vals.length} registros) -> Valores: ${JSON.stringify(vals)}`);
        }

        if (isAborted()) return;

        setChecklist(c);
        setTemplate(t);
        setTemplateRetired(modelRetired);
        setValuesRead({ values: checklistValuesToDraft(vals), version: readVersion });
        // NOVO LOG
        //UseChecklistEditorLogger.Main.info('[fetchData] Estados React definidos: checklist, template, values');
        setLoading(false);
      } catch (err: any) {
        if (isAborted()) return;
        // Uma falha temporária do refresh não desmonta o formulário em edição.
        if (!silent) setError(err?.message || 'Erro ao carregar checklist');
        // NOVO LOG
        UseChecklistEditorLogger.Main.info(`[fetchData] ERRO: ${err?.message || err}`);
        if (!silent) setLoading(false);
      }
    },
    [id, scope, companyId]
  );

  // Carrega dados inicial e quando "id" mudar
  useEffect(() => {
    const controller = new AbortController();
    mounted.current = true;
    setChecklist(null);
    setValuesRead({ values: null, version: 0 });
    // NOVO LOG
    //UseChecklistEditorLogger.Main.info('[useEffect] Montagem/ID change: disparando fetchData');
    fetchData(controller.signal);

    // Cleanup: aborta requisições pendentes
    return () => {
      // NOVO LOG
      UseChecklistEditorLogger.Main.info('[useEffect] Cleanup: abortando fetchData');
      controller.abort();
      mounted.current = false;
    };
  }, [fetchData]);

  // Expondo refetch sem sinal (caller não se preocupa com abort)
  const refetch = useCallback(() => fetchData(), [fetchData]);

  // O contador só sinaliza commits locais; a leitura mantém a conciliação de rascunhos existente.
  useEffect(() => {
    if (syncRevision === undefined || !syncEnabled || loading || checklist?.id !== id) return;
    const controller = new AbortController();
    const timer = setTimeout(() => { void fetchData(controller.signal, true); }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [syncRevision, syncEnabled, loading, checklist?.id, id, fetchData]);

  return { checklist, template, templateRetired, values: valuesRead.values, valuesReadVersion: valuesRead.version,
    markValuesWriteConfirmed, loading, error, refetch, refreshChecklistOnly };
} 
